/**
 * Notifications Hooks
 *
 * React Query hooks for notification operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import {
  NotificationsService,
  Notification,
  NotificationsResponse,
} from '../services/notifications.service';
import { communicationsKeys } from './communications-keys';

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params?: {
    unreadOnly?: boolean;
    status?: 'unread' | 'read' | 'dismissed';
    page?: number;
    pageSize?: number;
  }) => [...notificationKeys.all, 'list', params] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get notifications list
 */
export function useNotifications(params?: {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
  status?: 'unread' | 'read' | 'dismissed';
}) {
  const client = useApiClient();
  const service = new NotificationsService(client);

  return useQuery<NotificationsResponse>({
    queryKey: notificationKeys.list(params),
    queryFn: () => service.getNotifications(params),
  });
}

/**
 * Get unread notification count (lighter query)
 */
export function useUnreadNotificationCount() {
  const client = useApiClient();
  const service = new NotificationsService(client);

  return useQuery<number>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await service.getNotifications({ unreadOnly: true });
      return response.unreadCount;
    },
    refetchInterval: (query) => (query.state.status === 'error' ? false : 60_000),
  });
}

/**
 * Every cached `useNotifications` result, whatever params produced it.
 *
 * ─── Why this exists (W8.2 · F49) ─────────────────────────────────────────
 *
 * The optimistic mark-read read and wrote `notificationKeys.list({})` — the
 * key an EMPTY params object hashes to. No caller has ever produced it:
 *
 *   journey-web / business-web bell  { unreadOnly } | { status: 'dismissed' }
 *   business-web page                undefined
 *   citizen-mobile screen            undefined
 *
 * React Query hashes keys deterministically, and `{}`, `undefined` and
 * `{ unreadOnly: true }` all hash differently. So `getQueryData` returned
 * `undefined`, the `if (previousData)` guard was always false, `setQueryData`
 * NEVER RAN, and the rollback in `onError` was dead for the same reason. The
 * whole optimistic path was inert: a card only flipped to read after the
 * `invalidateQueries` refetch came back, which is the visible lag F49's
 * "expanding marks read" would have shipped with.
 *
 * The fix is not a better guess at the params. There is no single right key —
 * three variants are live at once in the bell alone — so the update has to walk
 * every cached list, which is what `setQueriesData` with a PREFIX does.
 */
const notificationListPrefix = [...notificationKeys.all, 'list'] as const;

/**
 * Mark notification as read.
 *
 * Optimistic across every cached list and the unread badge, so a card flips the
 * instant it is tapped rather than one round-trip later.
 */
export function useMarkNotificationAsRead() {
  const client = useApiClient();
  const service = new NotificationsService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => service.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      // A card's state is also its inbox row's: the one unread number moves too.
      queryClient.invalidateQueries({ queryKey: communicationsKeys.inbox });
    },
    onMutate: async (notificationId) => {
      // In flight refetches would land AFTER the optimistic write and undo it.
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      // Snapshot EVERY list we are about to touch, keyed by its own query key,
      // so the rollback can restore each one to exactly what it held. A single
      // `previousData` could only ever restore one of them.
      const previousLists = queryClient.getQueriesData<NotificationsResponse>({
        queryKey: notificationListPrefix,
      });
      const previousUnread = queryClient.getQueryData<number>(notificationKeys.unreadCount());

      // Whether this id is actually IN a given list decides whether that list's
      // `unreadCount` moves. A dismissed-view cache that never held the row
      // must not have its counter decremented.
      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: notificationListPrefix },
        (current) => {
          if (!current?.notifications) return current;
          const target = current.notifications.find((n: Notification) => n.id === notificationId);
          if (!target || target.read) return current;
          return {
            ...current,
            notifications: current.notifications.map((n: Notification) =>
              n.id === notificationId ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, current.unreadCount - 1),
          };
        }
      );

      // The badge is its own query with its own key, and it was never updated
      // at all — so the count stayed stale for up to its 60s refetch interval
      // while the row beneath it already read as read.
      if (typeof previousUnread === 'number') {
        queryClient.setQueryData<number>(notificationKeys.unreadCount(), (n) =>
          typeof n === 'number' ? Math.max(0, n - 1) : n
        );
      }

      return { previousLists, previousUnread };
    },
    onError: (_err, _notificationId, context) => {
      // Restore each list to its own snapshot, not all of them to one.
      for (const [key, data] of context?.previousLists ?? []) {
        queryClient.setQueryData(key, data);
      }
      if (typeof context?.previousUnread === 'number') {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousUnread);
      }
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const client = useApiClient();
  const service = new NotificationsService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => service.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      // A card's state is also its inbox row's: the one unread number moves too.
      queryClient.invalidateQueries({ queryKey: communicationsKeys.inbox });
    },
  });
}

/** Dismiss (delete) one notification. */
export function useDeleteNotification() {
  const client = useApiClient();
  const service = new NotificationsService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => service.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      // A card's state is also its inbox row's: the one unread number moves too.
      queryClient.invalidateQueries({ queryKey: communicationsKeys.inbox });
    },
  });
}

/** Restore a dismissed notification back into the feed (undo). */
export function useRestoreNotification() {
  const client = useApiClient();
  const service = new NotificationsService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => service.restoreNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      // A card's state is also its inbox row's: the one unread number moves too.
      queryClient.invalidateQueries({ queryKey: communicationsKeys.inbox });
    },
  });
}

/** Dismiss (delete) all read notifications. */
export function useDeleteAllReadNotifications() {
  const client = useApiClient();
  const service = new NotificationsService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => service.deleteAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      // A card's state is also its inbox row's: the one unread number moves too.
      queryClient.invalidateQueries({ queryKey: communicationsKeys.inbox });
    },
  });
}

// Re-export types
export type { Notification, NotificationsResponse };
